import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import {
  LetterTile,
  PopIn,
  RoundCounter,
  ShakeWrap,
  Tile,
  captionPill,
  gameShell,
  pieceButtonProps,
  seededShuffle,
  useCompleteOnce,
  useDragAndTap,
  useLetterAccent,
  useWrongFlash,
} from "./engineUtils";

/**
 * Sentence builder: word chips drag (or tap) into the sentence rail, left to
 * right. A finished sentence lights up word by word and is read aloud.
 * `interaction: "tapInOrder"` configs are tap-only. Completes after roundSize
 * (or all) sentences.
 */
export default function SentenceBuilderGame({ letter, config, speak, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { wrongId, flash } = useWrongFlash();
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const { accent: letterAccent, deep: letterDeep } = useLetterAccent(letter);
  const roundSize = config.roundSize || config.sentences?.length || 3;
  const sentences = useMemo(
    () => seededShuffle(config.sentences || [], 41).slice(0, roundSize),
    [config.sentences, roundSize]
  );
  const tapMode = config.interaction === "tapInOrder";
  const [si, setSi] = useState(0);
  const [filled, setFilled] = useState([]);
  const [pool, setPool] = useState([]);
  const [lit, setLit] = useState(-1);
  const [burst, setBurst] = useState(0);
  const timers = useRef([]);
  const doneRef = useRef(false);

  const current = sentences[si];
  const words = useMemo(() => current?.words || [], [current]);
  const focusWord = current?.focusWord || current?.focus?.word;
  const focusAsset = current?.focus?.assetName || current?.pictureAssetName || config.focusAssets?.[focusWord];
  const focusLabel = current?.pictureLabel || focusWord;

  const later = useCallback((fn, ms) => { const t = setTimeout(fn, ms); timers.current.push(t); return t; }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  useEffect(() => {
    if (!current) return;
    doneRef.current = false;
    const seed = current.shuffleSeed || 10 + si;
    const choices = current.choices || current.words;
    setPool(seededShuffle(choices.map((w, i) => ({ w, id: `${w}-${i}` })), seed));
    setFilled(Array(words.length).fill(null));
    setLit(-1);
    later(() => speak?.(words.join(" ")), si === 0 ? 900 : 250);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [si]);

  useEffect(() => {
    if (!current && sentences.length) finish();
  }, [current, sentences.length, finish]);

  const nextNeeded = filled.findIndex((w) => w == null);

  const celebrate = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    const n = words.length;
    const step = reduceMotion ? 0 : 170;
    for (let i = 0; i < n; i += 1) later(() => { setLit(i); sfx?.("pop", { volume: 0.45, rate: 1 + i * 0.06 }); }, i * step + 120);
    later(() => { speak?.(words.join(" "), { interrupt: true }); sfx?.("sparkle", { volume: 0.7 }); setBurst((b) => b + 1); }, n * step + 200);
    later(() => {
      if (si + 1 >= sentences.length) finish();
      else setSi((x) => x + 1);
    }, n * step + (reduceMotion ? 500 : 1700));
  }, [finish, later, reduceMotion, sentences.length, sfx, si, speak, words]);

  const tryPlace = useCallback(
    (chip, slotIndex) => {
      if (doneRef.current || slotIndex < 0 || filled[slotIndex] != null) return false;
      if (words[slotIndex] !== chip.w) {
        flash(`slot-${slotIndex}`);
        flash(chip.id);
        onWrong?.();
        return false;
      }
      sfx?.("snap");
      const next = [...filled];
      next[slotIndex] = chip.w;
      setFilled(next);
      setPool((p) => p.filter((x) => x.id !== chip.id));
      speak?.(chip.w, { interrupt: true });
      if (next.every((w) => w != null)) celebrate();
      return true;
    },
    [celebrate, filled, flash, onWrong, sfx, speak, words]
  );

  const onDrop = useCallback(
    ({ payload, targetId, targetData }) => {
      if (!targetId || !payload) return false;
      return tryPlace(payload, targetData?.index ?? -1);
    },
    [tryPlace]
  );
  const drag = useDragAndTap({ reduceMotion, sfx, onDrop });

  if (!current) return <div style={gameShell.root} />;

  // Word blocks are 0.8 tiles tall (wide, wooden-sticker style); the rail's
  // empty spaces are the same size so a block lands exactly where it fits.
  const CHIP = 0.8;
  const chipRadius = `calc(var(--tile) * ${CHIP} * 0.22)`;
  const renderGhost = (p) => <LetterTile text={p.w} wide scale={CHIP} />;
  const solvedAll = lit >= words.length - 1;

  return (
    <div style={{ ...gameShell.root, gap: compact ? 10 : 16 }}>
      <ConfettiBurst key={burst} active={burst > 0} originY={30} count={24} reduceMotion={reduceMotion} />
      <RoundCounter done={si} total={sentences.length} accent={letterAccent} />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={si}
          initial={reduceMotion ? false : { x: 60, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={reduceMotion ? undefined : { x: -60, opacity: 0, transition: { duration: 0.18 } }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden", paddingTop: compact ? 30 : 0 }}
        >
        <div style={{ margin: "auto", width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: compact ? 14 : 22, padding: "4px 0" }}>
          {focusAsset && (
            <PopIn reduceMotion={reduceMotion} style={{ flexDirection: "column", alignItems: "center" }}>
              <button
                type="button"
                className="learn-piece"
                aria-label={`Hear ${focusLabel}`}
                onClick={() => { sfx?.("pop", { volume: 0.5 }); speak?.(focusWord || focusLabel, { interrupt: true }); }}
                style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", borderRadius: "calc(var(--tile) * 0.4)" }}
              >
                <Tile letter={letter} assetName={focusAsset} alt={focusLabel || ""} scale={compact ? 1.5 : 1.8} lift={false} solved={solvedAll} />
              </button>
              {focusLabel && (
                <span
                  style={{
                    ...captionPill({ accent: letterAccent, deep: letterDeep, size: compact ? "sm" : "md" }),
                    marginTop: -14, position: "relative", zIndex: 2,
                    color: solvedAll ? LEARN.color.mintDeep : LEARN.color.ink,
                    borderColor: solvedAll ? `${LEARN.color.mint}88` : `${letterAccent}33`,
                    transition: "color 200ms ease, border-color 200ms ease",
                  }}
                >
                  {focusLabel}
                </span>
              )}
            </PopIn>
          )}

          {/* Sentence rail */}
          <div
            aria-label="Sentence"
            style={{
              display: "flex", flexWrap: "wrap", gap: compact ? 8 : 10, alignItems: "center", justifyContent: "center",
              padding: compact ? "12px" : "16px 20px", maxWidth: "100%", boxSizing: "border-box",
              background: "rgba(255,255,255,0.92)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
              borderRadius: LEARN.radius.lg, boxShadow: LEARN.shadow.card, border: `2px solid ${LEARN.color.line}`,
            }}
          >
            {words.map((w, i) => (
              <Slot
                key={`${si}-${i}`}
                index={i}
                word={filled[i]}
                sample={w}
                showGhost={!tapMode}
                next={i === nextNeeded}
                lit={i <= lit}
                wrong={wrongId === `slot-${i}`}
                hover={drag.hoverId === `slot-${i}`}
                armed={Boolean(drag.picked) && filled[i] == null}
                registerTarget={drag.registerTarget}
                onTap={() => drag.tapTarget(`slot-${i}`, { index: i })}
                scale={CHIP}
                reduceMotion={reduceMotion}
              />
            ))}
          </div>

          {/* Word pool */}
          <div style={{ ...gameShell.row, gap: compact ? 10 : 14, flexShrink: 0, padding: "6px 0 4px" }} aria-label="Words">
            <AnimatePresence initial={false}>
              {pool.map((chip, i) => (
                <PopIn key={chip.id} index={i} reduceMotion={reduceMotion} layout exit={reduceMotion ? undefined : { scale: 0.3, opacity: 0, transition: { duration: 0.14 } }}>
                  <ShakeWrap shake={wrongId === chip.id} reduceMotion={reduceMotion} style={{ borderRadius: chipRadius }}>
                    <button
                      {...pieceButtonProps({
                        dragProps: tapMode ? null : drag.dragProps,
                        payload: chip,
                        render: renderGhost,
                        label: chip.w,
                        onTap: () => {
                          sfx?.("pop", { volume: 0.5 });
                          if (tapMode) { tryPlace(chip, nextNeeded); return; }
                          speak?.(chip.w, { interrupt: true });
                          drag.pick(chip.id, chip);
                        },
                        style: { borderRadius: chipRadius, cursor: tapMode ? "pointer" : "grab" },
                      })}
                      aria-pressed={drag.isPicked(chip.id)}
                    >
                      <LetterTile text={chip.w} wide scale={CHIP} selected={drag.isPicked(chip.id)} />
                    </button>
                  </ShakeWrap>
                </PopIn>
              ))}
            </AnimatePresence>
          </div>
        </div>
        </motion.div>
      </AnimatePresence>
      {!tapMode && <drag.GhostLayer />}
    </div>
  );
}

/**
 * One space in the rail. Empty: a dashed well the size of its word (drag mode
 * shows the word as a faint ghost, since the prompt asks for its *matching*
 * space; tap-in-order mode keeps it blank). Filled: a blue block that turns
 * mint as the sentence lights up.
 */
function Slot({ index, word, sample, showGhost, next, lit, wrong, hover, armed, registerTarget, onTap, scale, reduceMotion }) {
  const ref = useCallback((el) => registerTarget(`slot-${index}`, el, { index }), [registerTarget, index]);
  const filled = word != null;
  const size = `calc(var(--tile) * ${scale})`;
  const radius = `calc(var(--tile) * ${scale} * 0.22)`;
  const live = hover || (armed && !filled);
  return (
    <ShakeWrap shake={wrong} reduceMotion={reduceMotion} style={{ borderRadius: radius }}>
      <motion.div
        ref={filled ? undefined : ref}
        role={armed ? "button" : undefined}
        tabIndex={armed ? 0 : -1}
        aria-label={filled ? word : `Space ${index + 1}`}
        className={armed && !filled ? "learn-slot-armed" : undefined}
        onClick={armed ? onTap : undefined}
        onKeyDown={(e) => { if (armed && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onTap(); } }}
        animate={lit && !reduceMotion ? { scale: [1, 1.14, 1], y: [0, -8, 0] } : { scale: 1, y: 0 }}
        transition={{ duration: 0.34, ease: LEARN.motion.pop }}
        style={{
          "--accent": LEARN.color.blue,
          "--accent-soft": `${LEARN.color.blue}22`,
          "--accent-ring": `${LEARN.color.blue}33`,
          minWidth: size,
          height: size,
          padding: `0 calc(var(--tile) * ${scale} * 0.28)`,
          borderRadius: radius,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxSizing: "border-box",
          fontFamily: LEARN.font, fontWeight: 700, lineHeight: 1, whiteSpace: "nowrap",
          fontSize: `clamp(20px, calc(var(--tile) * ${scale} * 0.36), 30px)`,
          background: filled ? (lit ? LEARN.color.mint : LEARN.color.blue) : live ? `${LEARN.color.blue}1a` : "rgba(11,26,51,0.04)",
          color: filled ? "#fff" : showGhost ? (live ? `${LEARN.color.blue}88` : `${LEARN.color.ink}33`) : "transparent",
          border: filled ? `2px solid ${lit ? LEARN.color.mintDeep : LEARN.color.blueDeep}` : `3px ${hover ? "solid" : "dashed"} ${hover ? LEARN.color.blue : next || armed ? LEARN.color.sun : LEARN.color.line}`,
          boxShadow: filled
            ? `0 4px 0 ${lit ? LEARN.color.mintDeep : LEARN.color.blueDeep}, ${LEARN.shadow.card}`
            : hover ? `0 0 0 6px ${LEARN.color.blue}33` : "none",
          textShadow: filled ? "0 2px 0 rgba(11,26,51,0.18)" : "none",
          cursor: armed ? "pointer" : "default",
          transform: hover ? "scale(1.04)" : "none",
          transition: "background 160ms ease, color 160ms ease, box-shadow 160ms ease, border-color 160ms ease, transform 160ms ease",
        }}
      >
        {filled ? word : sample}
      </motion.div>
    </ShakeWrap>
  );
}
